import { compileSurveyDocument } from '@apruv/survey-contract/compiler';
import { resolveFlags } from '@apruv/survey-contract/flags';
import type { SurveyDocument } from '@apruv/survey-contract/types';
import type { CXPublicSurveyResponse } from '../../services/cxPublicSurveyService';
import type { CXRuntimeContext } from '../../services/cxRuntimeContext';
import { previewReviewRouting } from './previewReviewRouting';
import { markCanonicalPreview } from './canonicalPreviewContext';

export interface CanonicalPreviewScenario {
  requestedFlags?: Record<string, boolean>;
  locale: string;
  scoreMethod: 'nps' | 'csat';
  serviceMedia: CXRuntimeContext['serviceMedia'];
  technicianNotes: string;
  technicianFirstName?: string;
  companyName: string;
  locationName: string;
  tippingEligible: boolean;
  serviceLifecycle?: 'initial_service' | 'regular_service';
  contactEligible?: boolean;
  reviewAvailability?: 'sample' | 'unavailable' | 'matched' | 'matched_no_alternatives';
  reviewDraw?: number;
  existingResponse?: CXPublicSurveyResponse['existingResponse'];
}

/** Synthetic invitation context is constructed here; callers never supply execution credentials. */
export function prepareCanonicalPreview(
  document: SurveyDocument,
  scenario: CanonicalPreviewScenario,
  initialScreenId?: string
) {
  const compiled = compileSurveyDocument(document);
  if (document.presentation !== 'feedback_2026')
    throw new Error('Choose the new survey presentation.');
  if (!document.configuration.localization.supportedLocales.includes(scenario.locale))
    throw new Error('This locale is not configured for the survey.');
  if (initialScreenId && !compiled.snapshot.screens.some((screen) => screen.id === initialScreenId))
    throw new Error('The preview screen no longer exists.');
  const identity = compiled.snapshot.identity;
  const requestedFlags =
    scenario.requestedFlags ?? (Object.create(null) as Record<string, boolean>);
  const flagSnapshot = {
    ...resolveFlags(document, identity, requestedFlags).toJSON(),
    resolverVersion: 'survey-flags/v1',
    eventFlowVersionId: 'preview-flow',
    runId: 'preview-run',
    stepId: 'preview-step',
    requestedFlags: structuredClone(requestedFlags),
  };
  const response: CXPublicSurveyResponse = {
    appointmentCode: 'preview',
    executionStepId: 'preview-step',
    triggerGroupId: 'preview-run',
    moduleRevisionId: identity.experienceVersionId,
    triggerEvent: 'preview',
    surveyDocument: document,
    persistenceDisabled: true,
    existingResponse: scenario.existingResponse,
    sourceEvent: {
      payload: {
        raw: {
          scoreMethod: scenario.scoreMethod.toUpperCase(),
          ...(scenario.serviceLifecycle ? { serviceLifecycle: scenario.serviceLifecycle } : {}),
          ...(scenario.technicianFirstName
            ? { technicianFirstName: scenario.technicianFirstName }
            : {}),
          companyName: scenario.companyName,
          locationName: scenario.locationName,
        },
      },
    },
    runtimeContext: {
      organizationId: identity.organizationId,
      ...(scenario.reviewAvailability === undefined
        ? {}
        : {
            reviewRouting: previewReviewRouting(
              document,
              scenario.reviewAvailability,
              scenario.reviewDraw
            ),
          }),
      serviceMedia: structuredClone(scenario.serviceMedia),
      technicianNotes: scenario.technicianNotes,
      contactPhone: scenario.contactEligible === true ? '+12085550123' : '',
      videoAssets: {},
      refresh: 'reload_invite',
      flagSnapshot,
      capabilities: {
        hasServiceMedia: scenario.serviceMedia.length > 0,
        hasTechnicianNotes: !!scenario.technicianNotes,
        canContact: scenario.contactEligible === true,
        tippingConfigured: scenario.tippingEligible,
        tipEligibility: scenario.tippingEligible ? 'server_check_required' : 'disabled',
      },
    },
  };
  markCanonicalPreview(response, { locale: scenario.locale, initialScreenId });
  return { compiled, response, flagSnapshot };
}
